import React, { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  RiPenNibLine,
  RiTeamLine,
  RiHistoryLine,
  RiDeleteBinLine,
  RiMailLine,
  RiBuildingLine,
  RiUserStarLine,
} from 'react-icons/ri';
import authorizedSignerService, { AuthorizedSigner } from '../../services/authorizedSignerService';
import { useDeleteAuthorizedSigner } from '../../hooks/mutations/useAuthorizedSignerMutations';
import { usePermission } from '../../hooks/usePermission';
import { queryKeys } from '../../queryClient';
import {
  AUTHORIZED_SIGNERS_PATH,
  authorizedSignerDetailUrl,
  authorizedSignerEditUrl,
} from '../../utils/authorizedSignerLinks';
import { companyDetailUrl } from '../../utils/companyLinks';
import { parseRouteId } from '../../utils/routeId';
import { showConfirm } from '../../utils/alert';
import {
  authorizedSignerToForm,
  AUTHORIZED_SIGNER_FIELD_LABELS,
  AUTHORIZED_SIGNER_VALUE_FORMATTERS,
} from '../../components/authorized-signer/authorizedSignerForm';
import {
  DetailPageLayout,
  DetailSection,
  InfoField,
  InfoGrid,
  RecordMetaCard,
  EntityActivityTimeline,
  RelatedRecordsCard,
  useDetailNavigation,
  detailNavState,
  headerButtonClass,
  headerButtonVariants,
} from '../../components/detail';
import { cn } from '../../utils/cn';

const LIST_PATH = AUTHORIZED_SIGNERS_PATH;
const RELATED_LIMIT = 10;

/**
 * หน้ารายละเอียดผู้มีอำนาจลงนาม — `/master-data/authorized-signers/:id`
 * (แทนโมดัล "รายละเอียดผู้มีอำนาจลงนาม" · มติ PO 28 ต.ค. 2026 · ต้นแบบ CompanyViewPage)
 *
 * ทุกช่องของฟอร์มเดิมแบบอ่านอย่างเดียว + ลิงก์ไปหน้าบริษัท (gate companies.view) + การ์ด "ผู้ลงนามอื่นของบริษัทนี้"
 * (ใช้ list API เดิมกรอง company_id — ไว้ดูว่าใครเป็นผู้ลงนามหลักของบริษัทเดียวกัน) + ข้อมูลการสร้าง/แก้ไข + ประวัติกิจกรรม
 * ไม่มีลายเซ็น/ไฟล์แนบในโมดูลนี้ (ตาราง authorized_signers ไม่มีคอลัมน์ไฟล์)
 */
const AuthorizedSignerViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const signerId = parseRouteId(id);
  const location = useLocation();
  const { hasPermission } = usePermission();
  const { backTo, goTo, navState } = useDetailNavigation(LIST_PATH);
  const deleteMutation = useDeleteAuthorizedSigner();
  const [activityRefresh, setActivityRefresh] = useState(0);

  const { data: signer, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.authorizedSigners.detail(signerId ?? 0),
    queryFn: () => authorizedSignerService.getAuthorizedSigner(signerId!),
    enabled: signerId !== null,
  });

  const hereState = detailNavState(location);

  const handleRefresh = () => {
    refetch();
    setActivityRefresh((n) => n + 1);
  };

  const handleDelete = () => {
    if (!signer) return;
    showConfirm(
      'ยืนยันการลบ',
      `คุณต้องการลบผู้มีอำนาจลงนาม ${signer.full_name} ใช่หรือไม่?`,
      async () => {
        try {
          await deleteMutation.mutateAsync(signer.id);
          goTo(backTo, { replace: true });
        } catch (e) {
          // hook แสดงข้อความจาก server แล้ว (เช่น เป็นผู้ลงนามในสัญญาที่ยังไม่สิ้นสุด)
          console.error('Error deleting signer:', e);
        }
      },
      { isDanger: true }
    );
  };

  const notFound = signerId === null ? { response: { status: 404 } } : null;
  const form = signer ? authorizedSignerToForm(signer) : null;
  const companyName = signer?.company?.name || (form?.company_id ? `#${form.company_id}` : null);

  return (
    <DetailPageLayout
      breadcrumbs={[{ label: 'ผู้มีอำนาจลงนาม', path: backTo }, { label: signer?.full_name || 'รายละเอียด' }]}
      icon={<RiPenNibLine />}
      title={signer?.full_name || 'รายละเอียดผู้มีอำนาจลงนาม'}
      recordId={signer?.id}
      subtitle={signer ? [signer.position, signer.company?.name].filter(Boolean).join(' · ') : 'รายละเอียดผู้มีอำนาจลงนาม'}
      backTo={backTo}
      onRefresh={handleRefresh}
      editTo={signer ? authorizedSignerEditUrl(signer.id) : undefined}
      editPermission="authorized_signers.edit"
      editState={navState}
      actions={
        signer && hasPermission('authorized_signers.delete') ? (
          <button type="button" onClick={handleDelete} className={cn(headerButtonClass, headerButtonVariants.danger)}>
            <RiDeleteBinLine className="h-4 w-4 mr-1.5" />
            ลบ
          </button>
        ) : undefined
      }
      loading={signerId !== null && isLoading}
      error={notFound || error}
      notFoundMessage="ไม่พบผู้มีอำนาจลงนามที่ระบุ (อาจถูกลบไปแล้ว)"
      onRetry={() => refetch()}
      sidebar={
        signer && (
          <>
            <RecordMetaCard
              createdAt={signer.created_at}
              createdBy={signer.created_by}
              updatedAt={signer.updated_at}
              updatedBy={signer.updated_by}
            />
            <DetailSection title="ประวัติกิจกรรม" icon={<RiHistoryLine />}>
              <EntityActivityTimeline
                entityType="authorized_signer"
                entityId={signer.id}
                refreshTrigger={activityRefresh}
                fieldLabels={AUTHORIZED_SIGNER_FIELD_LABELS}
                valueFormatters={AUTHORIZED_SIGNER_VALUE_FORMATTERS}
              />
            </DetailSection>
          </>
        )
      }
    >
      {signer && form && (
        <>
          <DetailSection
            title="ข้อมูลพื้นฐาน"
            icon={<RiUserStarLine />}
            actions={
              <div className="flex flex-wrap gap-2">
                {form.is_primary && (
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border bg-info-50 text-info-700 border-info-200"
                    data-testid="signer-primary-badge"
                  >
                    ผู้ลงนามหลัก
                  </span>
                )}
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border',
                    form.is_active
                      ? 'bg-success-50 text-success-700 border-success-200'
                      : 'bg-secondary-50 text-secondary-600 border-secondary-200'
                  )}
                  data-testid="signer-status-badge"
                >
                  <span className={cn('w-1.5 h-1.5 rounded-full', form.is_active ? 'bg-success-500' : 'bg-secondary-400')} />
                  {form.is_active ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                </span>
              </div>
            }
          >
            <InfoGrid>
              <InfoField label="บริษัท" icon={<RiBuildingLine className="h-4 w-4" />} testId="signer-company">
                {companyName ? (
                  hasPermission('companies.view') ? (
                    <Link
                      to={companyDetailUrl(form.company_id)}
                      state={hereState}
                      className="text-primary-600 hover:text-primary-800 [overflow-wrap:anywhere]"
                    >
                      {companyName}
                    </Link>
                  ) : (
                    <span className="[overflow-wrap:anywhere]">{companyName}</span>
                  )
                ) : undefined}
              </InfoField>
              <InfoField label="รหัสบริษัท" value={signer.company?.code} mono />
              <InfoField label="ชื่อ-นามสกุล" value={form.full_name} />
              <InfoField label="ตำแหน่ง" value={form.position} />
            </InfoGrid>
          </DetailSection>

          <DetailSection title="ข้อมูลติดต่อ" icon={<RiTeamLine />}>
            <InfoGrid>
              <InfoField label="อีเมล" value={form.email}>
                {form.email ? (
                  <a
                    href={`mailto:${form.email}`}
                    className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 [overflow-wrap:anywhere]"
                  >
                    <RiMailLine className="h-4 w-4 flex-shrink-0" />
                    {form.email}
                  </a>
                ) : undefined}
              </InfoField>
              <InfoField label="เบอร์โทรศัพท์" value={form.phone} mono />
              <InfoField
                label="ผู้ลงนามหลัก (สำหรับสัญญา)"
                value={form.is_primary ? 'ใช่' : 'ไม่ใช่'}
                hint="ผู้ลงนามหลักจะถูกใช้ในการสร้าง PDF สัญญาโดยอัตโนมัติ"
              />
              <InfoField label="สถานะ" value={form.is_active ? 'ใช้งาน' : 'ไม่ใช้งาน'} />
            </InfoGrid>
          </DetailSection>

          <RelatedRecordsCard<AuthorizedSigner>
            title="ผู้ลงนามอื่นของบริษัทนี้"
            icon={<RiPenNibLine />}
            permission="authorized_signers.view"
            queryKey={['authorized-signer-related', signer.id, 'company', form.company_id]}
            fetcher={async () => {
              // list API เดิมกรอง company_id · ตัดตัวเองออก (limit+1 ให้ยังได้ครบ RELATED_LIMIT แถว)
              const res = await authorizedSignerService.getAuthorizedSigners({
                company_id: form.company_id,
                limit: RELATED_LIMIT + 1,
              });
              const others = res.items.filter((s) => s.id !== signer.id).slice(0, RELATED_LIMIT);
              const selfInPage = res.items.some((s) => s.id === signer.id);
              return { items: others, total: Math.max(0, res.total - (selfInPage ? 1 : 0)) };
            }}
            rowKey={(s) => s.id}
            rowTo={(s) => authorizedSignerDetailUrl(s.id)}
            rowState={navState}
            viewAllTo={`${LIST_PATH}?company=${form.company_id}`}
            emptyText="บริษัทนี้ไม่มีผู้ลงนามคนอื่น"
            testId="signer-related-company-signers"
            renderRow={(s) => (
              <>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-secondary-900 [overflow-wrap:anywhere]">{s.full_name}</p>
                  <p className="text-xs text-secondary-500 [overflow-wrap:anywhere]">{s.position}</p>
                </div>
                <span className="flex flex-shrink-0 items-center gap-1.5 text-xs">
                  {s.is_primary && <span className="text-info-700">ผู้ลงนามหลัก</span>}
                  <span className={s.is_active ? 'text-success-700' : 'text-secondary-500'}>
                    {s.is_active ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                  </span>
                </span>
              </>
            )}
          />
        </>
      )}
    </DetailPageLayout>
  );
};

export default AuthorizedSignerViewPage;
